// ============================================================================
// AestheticClinic — Phase 18 rework
// Route: /aesthetic-clinic-photography
//
// Repositioned as a tight, scannable offer sheet for summer clinic / aesthetic
// content sessions. Cold-traffic conversion is the only job — packages live
// directly under the hero, the existing "Selected work" stills gallery and
// "Motion" video block are MOVED INTACT below the packages (media files and
// internal order untouched), and the long marketing intro sections ("The
// Opportunity" and the standalone "What's included" checklist) are removed.
//
// Pricing format changed from struck-through promo prices to clean text:
//   "Usually $___"  (small, muted)
//   "Summer rate: $___"  (oxblood serif, primary)
//
// Hero copy refreshed to speak directly to aesthetic clinics (page <title>
// and meta description deliberately unchanged — wellness / dental SEO
// targeting stays intact per the brief).
//
// Hard constraints honored:
//   - Media files / internal order untouched (we moved entire blocks, not
//     individual photos or clips)
//   - All existing /inquire?source=* tracking tags preserved exactly
//   - Brand fonts, colors, components reused (Section, HeroVideo, MediaGallery,
//     VideoGallerySection, TestimonialCard)
//
// Reused infrastructure:
//   - <HeroVideo>          Phase 11.3 three-layer architecture
//   - <Section> primitives Phase 10 spacing scale (tighter `tight`/`normal`)
//   - <MediaGallery>       masonry + PhotoSwipe lightbox
//   - <TestimonialCard>    backdrop-aware
//   - <VideoGallerySection> auto-hides until ≥1 clip is published
//   - useTestimonials("business")
//   - useClinicSelectedWork() — clinic-selected-work Sanity slug
//   - useClinicVideo()        — clinic-video Sanity slug
//   - useClinicHero()         — aesthetic-clinic-hero heroMedia slug
// ============================================================================

import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { HeroVideo, heroTextShadow } from "@/components/HeroVideo";
import { MediaGallery } from "@/components/MediaGallery";
import { TestimonialCard } from "@/components/TestimonialCard";
import { VideoGallerySection } from "@/components/VideoGallerySection";
import { AboutElizavetaSection, ABOUT_MIDDLE } from "@/components/AboutElizavetaSection";
import { Separator } from "@/components/ui/separator";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTestimonials, useClinicSelectedWork, useClinicVideo, useClinicHero } from "@/lib/content";

// ─── Packages (Phase 18 rework) ────────────────────────────────────────────
//
// Three summer-rate cards. Source tags (clinic-refresh / clinic-bcd /
// clinic-bcv) are preserved exactly so inquiry-attribution tracking
// keeps working. Visible card / CTA names updated to match the new
// package names from the brief.
//
// Pricing notation: clean text "Usually $X / Summer rate: $Y" — no
// strikethrough. "Summer rate" is the primary (oxblood serif), "Usually"
// is a small muted hint above it.
//
// Phase 18.2 — outcome-first include lists. Prices, names, Most Popular
// badge, deposit/payment lines, and ?source= tags are intentionally
// unchanged; only the `positioning` (intro line) and `includes` arrays
// were rewritten so each card leads with the deliverables the buyer
// actually receives, with logistics (session length, location count,
// people limit) listed last. Image counts bumped per brief:
//   Business Refresh   20 → 30 edited images
//   Brand Content      45 → 50 edited images (now first bullet)
//   Photo + Video      unchanged (60 photos) but now first bullet
const PACKAGES = [
    {
        id: "business-refresh",
        name: "Business Refresh",
        badge: null,
        regular: "$850",
        promo: "$495",
        paymentLines: ["$495 paid in full to reserve"],
        positioning:
            "A quick, polished refresh — current provider headshots plus a few clean space and detail shots.",
        includes: [
            "30 professionally edited images",
            "Retouched + color-graded, ready for website, Google profile, and social",
            "60-minute session — one provider or small practice",
            "1–2 looks / backdrops",
            "Guided posing so nobody feels stiff",
            "Light planning + shot list",
        ],
        cta: "Request Business Refresh",
        source: "clinic-refresh",
    },
    {
        id: "brand-content-session",
        name: "Brand Content Session",
        badge: "Most Popular",
        regular: "$1,450",
        promo: "$950",
        paymentLines: [
            "$475 reserves your session date",
            "$475 balance due before the shoot",
        ],
        positioning:
            "A full content library for your practice — portraits, space, service, and details, plus short reels.",
        includes: [
            "50 professionally edited images",
            "2 edited vertical reels (15–30s) for Instagram / TikTok",
            "Retouched + color-graded, ready for website, Google profile, and social",
            "Provider / team portraits, workspace & interiors, service / process, product details",
            "3-hour photo + light video session",
            "Up to 4 people",
            "1 location (or 2 nearby)",
            "Planning call + shot list",
        ],
        cta: "Request Brand Content Session",
        source: "clinic-bcd",
    },
    {
        id: "photo-video-session",
        name: "Photo + Video Session",
        badge: null,
        regular: "$1,850",
        promo: "$1,350",
        paymentLines: [
            "$675 reserves your session date",
            "$675 balance due before the shoot",
        ],
        positioning:
            "Your biggest content drop — a full photo set, a highlight promo video, and a batch of social clips.",
        includes: [
            "60 professionally edited photos",
            "Clinic/business highlight promo video (up to 90s)",
            "Up to 3 edited vertical reels (15–30s) + 15–20 short raw/social clips",
            "Everything in Brand Content Session",
            "4-hour photo + video session",
            "Up to 5 people",
            "1 location (or 2 nearby)",
            "Optional captions / text overlay",
        ],
        cta: "Request Photo + Video Session",
        source: "clinic-bcv",
    },
];

// ─── Card component ────────────────────────────────────────────────────────

function PackageCard({ pkg }) {
    const [includesOpen, setIncludesOpen] = useState(false);
    const panelId = `clinic-pkg-includes-${pkg.id}`;
    const inquireHref = `/inquire?source=${encodeURIComponent(pkg.source)}`;

    return (
        <article
            data-testid={`clinic-package-${pkg.id}`}
            data-badge={pkg.badge || "false"}
            className={cn(
                "relative flex flex-col h-full bg-background border rounded-[3px] p-6 sm:p-7 transition-all",
                pkg.badge
                    ? "border-[hsl(var(--lux-oxblood))] shadow-[0_24px_60px_-30px_rgba(74,24,32,0.35)] ring-1 ring-[hsl(var(--lux-oxblood))]/15"
                    : "border-border hover:border-foreground/40"
            )}
        >
            {pkg.badge ? (
                <span
                    data-testid={`clinic-package-badge-${pkg.id}`}
                    className="absolute -top-3 left-6 inline-flex items-center bg-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-porcelain))] text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded-full"
                >
                    {pkg.badge}
                </span>
            ) : null}

            <h3 className="font-serif-display text-2xl leading-tight mt-1">
                {pkg.name}
            </h3>

            {/* Price block — Phase 18: clean text, no strikethrough.
              * Top: "Usually $___" small muted hint.
              * Bottom: "Summer rate: $___" with the summer rate as the
              * primary oxblood serif value. */}
            <div className="mt-3">
                <div
                    data-testid={`clinic-package-regular-${pkg.id}`}
                    className="text-[12px] tracking-[0.03em] text-muted-foreground"
                >
                    Usually {pkg.regular}
                </div>
                <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                    <span className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--lux-brass))]">
                        Summer rate
                    </span>
                    <span
                        data-testid={`clinic-package-promo-${pkg.id}`}
                        className="text-[hsl(var(--lux-oxblood))] font-serif-display text-[26px] leading-none"
                    >
                        {pkg.promo}
                    </span>
                </div>
            </div>

            {/* Payment lines */}
            <div className="mt-3 space-y-1 text-[12.5px] tracking-[0.02em] text-muted-foreground">
                {pkg.paymentLines.map((line, idx) => (
                    <div
                        key={line}
                        data-testid={`clinic-package-payment${idx + 1}-${pkg.id}`}
                    >
                        {line}
                    </div>
                ))}
            </div>

            <Separator className="my-5 bg-[hsl(var(--lux-brass))]/45 w-8" />

            {/* Positioning sentence — single line, practical, replaces the
              * old "Best for" pattern. */}
            <p
                data-testid={`clinic-package-positioning-${pkg.id}`}
                className="text-[13px] leading-[1.7] text-foreground/85"
            >
                {pkg.positioning}
            </p>

            {/* Includes — collapsible on mobile (<md), always-open on
              * desktop. Matches the home-services / business pattern. */}
            <div className="mt-5 border-t border-border/60 pt-4 md:border-t-0 md:pt-0">
                <button
                    type="button"
                    onClick={() => setIncludesOpen((v) => !v)}
                    aria-expanded={includesOpen}
                    aria-controls={panelId}
                    data-testid={`clinic-package-includes-toggle-${pkg.id}`}
                    className="flex items-center justify-between w-full text-left md:cursor-default md:pointer-events-none"
                >
                    <span className="kicker">Includes</span>
                    <ChevronDown
                        aria-hidden
                        className={cn(
                            "h-4 w-4 text-muted-foreground transition-transform duration-200 md:hidden",
                            includesOpen && "rotate-180"
                        )}
                    />
                </button>
                <div
                    id={panelId}
                    data-testid={`clinic-package-included-${pkg.id}`}
                    className={cn(
                        "overflow-hidden transition-[max-height,opacity] duration-200 ease-out",
                        includesOpen ? "max-h-[1400px] opacity-100" : "max-h-0 opacity-0",
                        "md:!max-h-none md:!opacity-100"
                    )}
                >
                    <ul className="mt-3 space-y-2 text-[13px]">
                        {pkg.includes.map((it) => (
                            <li key={it} className="flex items-start gap-2">
                                <Check
                                    aria-hidden
                                    className="h-3.5 w-3.5 mt-1.5 text-[hsl(var(--lux-brass))] shrink-0"
                                />
                                <span className="leading-[1.6]">{it}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-auto pt-6">
                <Link
                    to={inquireHref}
                    data-testid={`clinic-package-cta-${pkg.id}`}
                    onClick={() => {
                        if (typeof window !== "undefined") {
                            window.scrollTo({ top: 0, behavior: "instant" });
                        }
                    }}
                    className={cn(
                        "btn-request btn-request--sm w-full justify-center",
                        !pkg.badge && "btn-request--ghost"
                    )}
                >
                    {pkg.cta}
                </Link>
            </div>
        </article>
    );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function AestheticClinic() {
    const allBusinessTestimonials = useTestimonials("business");
    const testimonials = (allBusinessTestimonials || []).slice(0, 3);

    const galleryItems = useClinicSelectedWork();
    const clinicVideoItems = useClinicVideo();
    const clinicHeroMedia = useClinicHero();

    return (
        <div className="relative" data-testid="clinic-page">
            {/* SEO targeting (wellness/dental) deliberately unchanged. */}
            <Seo {...SEO.aestheticClinic} />

            {/* ─── 1. HERO ─────────────────────────────────────────────── */}
            <HeroVideo
                dataTestId="clinic-hero"
                media={clinicHeroMedia}
                ariaLabel="Photography and video for aesthetic clinics, wellness practices, and dental offices by Elizaveta von Stuben Imagery."
            >
                <span
                    className="block font-sans uppercase text-[11px] sm:text-[12px]"
                    style={{
                        color: "#e6e1db",
                        letterSpacing: "0.22em",
                        ...heroTextShadow.light,
                    }}
                >
                    Aesthetic, Wellness &amp; Dental &middot; Arizona
                </span>
                <h1
                    className="font-serif-display mt-3 sm:mt-4 text-[clamp(31px,7.6vw,34px)] sm:text-[clamp(46px,5.2vw,76px)]"
                    data-testid="clinic-hero-headline"
                    style={{
                        color: "#fbf8f4",
                        lineHeight: 1.04,
                        letterSpacing: "0",
                        maxWidth: "780px",
                        ...heroTextShadow.heavy,
                    }}
                >
                    Photography + Video for Aesthetic Clinics
                </h1>
                <p
                    className="mt-4 sm:mt-6 text-[15px] sm:text-[17px] leading-[1.55]"
                    data-testid="clinic-hero-positioning"
                    style={{
                        color: "#f5f1eb",
                        maxWidth: "640px",
                        ...heroTextShadow.medium,
                    }}
                >
                    Clean, current visuals for your website, Google
                    profile, booking pages, and social content.
                </p>
                <div className="mt-6 sm:mt-8 flex flex-wrap gap-3">
                    <a
                        href="#clinic-packages"
                        data-testid="clinic-hero-primary-cta"
                        className="btn-request"
                        style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.35)" }}
                    >
                        View Summer Packages
                    </a>
                    <Link
                        to="/inquire?source=clinic-landing"
                        data-testid="clinic-hero-secondary-cta"
                        className="btn-request btn-request--sm btn-request--outline-light"
                        style={{
                            borderColor: "rgba(251,248,244,0.65)",
                            color: "#fbf8f4",
                            boxShadow: "0 6px 22px rgba(0,0,0,0.25)",
                        }}
                    >
                        Request Availability
                    </Link>
                </div>
                <p
                    data-testid="clinic-hero-availability-note"
                    className="mt-4 sm:mt-5 text-[12px] sm:text-[13px] leading-[1.55]"
                    style={{
                        color: "#e6e1db",
                        maxWidth: "560px",
                        ...heroTextShadow.light,
                    }}
                >
                    Arizona summer rates available for a limited number
                    of June/July sessions.
                </p>
            </HeroVideo>

            {/* ─── 2. SHORT INTRO ─────────────────────────────────────────
              * Single tight paragraph — gives cold traffic enough context
              * to understand what's on offer without padding the page.
              */}
            <Section
                spacing="tight"
                className="border-t border-border"
                data-testid="clinic-intro"
            >
                <p className="mx-auto max-w-[68ch] text-center text-[15px] sm:text-base leading-[1.8] text-foreground/85">
                    Most clinics do not need a huge production. They need
                    current provider portraits, clean room details,
                    natural team images, treatment/product details, and
                    a few short clips that make the practice feel active
                    and trustworthy online.
                </p>
            </Section>

            {/* ─── 3. PACKAGES ────────────────────────────────────────────
              * Moved directly under the hero/intro per Phase 18 brief.
              * Three cards, summer-rate primary, Most Popular badge on
              * Brand Content Session.
              */}
            <Section
                spacing="normal"
                id="clinic-packages"
                data-testid="clinic-packages"
                className="scroll-mt-24 border-t border-border bg-[hsl(var(--lux-porcelain))]"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-8">
                    <div className="lg:col-span-7">
                        <Kicker>Summer packages</Kicker>
                        <SerifTitle className="mt-3">
                            Three ways to start.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Practical sessions built around how your practice
                        actually uses content &mdash; website, Google
                        profile, booking pages, and social.
                    </p>
                </div>

                <div
                    data-testid="clinic-packages-grid"
                    className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-8"
                >
                    {PACKAGES.map((p) => (
                        <PackageCard key={p.id} pkg={p} />
                    ))}
                </div>

                {/* Soft, date-free urgency line under the cards
                  * (Phase 18 — replaces the June 18, 2026 hard date). */}
                <p
                    data-testid="clinic-urgency-line"
                    className="mt-8 sm:mt-10 text-center text-[13px] leading-[1.7] text-muted-foreground max-w-[64ch] mx-auto"
                >
                    A limited number of summer sessions are open across
                    Phoenix and Scottsdale. A reservation payment holds
                    your date.
                </p>

                {/* Custom-session note + repeated CTA strip — Phase 18 (3b).
                  * Catches conversion intent right at the decision point
                  * before the gallery scrolls into view. */}
                <div
                    data-testid="clinic-post-packages-cta"
                    className="mt-8 sm:mt-10 mx-auto max-w-[680px] text-center"
                >
                    <p className="text-[14px] sm:text-[15px] leading-[1.8] text-foreground/85">
                        Need something specific? I can tailor the scope,
                        deliverables, and pricing to your practice &mdash;
                        just ask.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-3">
                        <Link
                            to="/inquire?source=clinic-landing"
                            data-testid="clinic-post-packages-primary"
                            onClick={() => {
                                if (typeof window !== "undefined") {
                                    window.scrollTo({ top: 0, behavior: "instant" });
                                }
                            }}
                            className="btn-request btn-request--sm"
                        >
                            Request Availability
                        </Link>
                        <Link
                            to="/inquire?source=clinic-custom"
                            data-testid="clinic-custom-cta"
                            onClick={() => {
                                if (typeof window !== "undefined") {
                                    window.scrollTo({ top: 0, behavior: "instant" });
                                }
                            }}
                            className="btn-request btn-request--sm btn-request--ghost"
                        >
                            Ask about a custom session
                        </Link>
                    </div>
                </div>
            </Section>

            {/* ─── 4. SELECTED WORK GALLERY (moved INTACT below packages) ─
              * Same Sanity slug (clinic-selected-work), same SWR fallback,
              * same items in same order. Only the page-level position
              * changed.
              */}
            <Section
                spacing="normal"
                className="border-t border-border"
                data-testid="clinic-gallery"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10">
                    <div className="lg:col-span-7">
                        <Kicker>Selected work</Kicker>
                        <SerifTitle className="mt-3">
                            Polished, current, and ready to publish.
                        </SerifTitle>
                    </div>
                    <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                        Portraits, workspace, product, and brand details
                        &mdash; work I&apos;ve made for clients across
                        business, wellness, and brand contexts.
                    </p>
                </div>
                {galleryItems && galleryItems.length > 0 ? (
                    <MediaGallery
                        items={galleryItems}
                        initialCount={8}
                        incrementCount={8}
                        desktopColumns={3}
                        galleryId="clinic-gallery"
                        defaultInquiryType="Clinic Content Session"
                    />
                ) : null}
            </Section>

            {/* ─── 5. MOTION (moved INTACT below gallery) ──────────────── */}
            <VideoGallerySection
                items={clinicVideoItems}
                testId="clinic-video"
                galleryId="clinic-video-gallery"
                inquiryType="Clinic Content Session"
                className="border-t border-border"
            />

            {/* ─── 6. TESTIMONIALS ──────────────────────────────────────── */}
            {testimonials.length > 0 ? (
                <section
                    data-testid="clinic-testimonials"
                    className="border-t border-border"
                >
                    <Section spacing="normal">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10 sm:mb-12">
                            <div className="lg:col-span-7">
                                <Kicker>What clients say</Kicker>
                                <SerifTitle className="mt-3">
                                    Calm, professional, and on brand.
                                </SerifTitle>
                            </div>
                            <p className="lg:col-span-5 text-sm leading-[1.85] text-muted-foreground max-w-[52ch]">
                                A small set of clients who&apos;ve worked
                                with the studio for brand and content
                                photography &mdash; in their own words.
                            </p>
                        </div>
                        <div
                            className={[
                                "grid gap-6 sm:gap-8",
                                testimonials.length === 1
                                    ? "grid-cols-1 max-w-[640px]"
                                    : testimonials.length === 2
                                        ? "grid-cols-1 md:grid-cols-2"
                                        : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
                            ].join(" ")}
                        >
                            {testimonials.map((r) => (
                                <TestimonialCard
                                    key={r.id}
                                    testimonial={r}
                                    testId={`clinic-testimonial-${r.id}`}
                                />
                            ))}
                        </div>
                    </Section>
                </section>
            ) : null}

            {/* ─── 7. PAGE CLOSER — ABOUT ELIZAVETA ─────────────────────
              * Phase 18 (cleanup): the legacy "Let's work together /
              * Reserve Your Session" final CTA block was removed; the
              * About section is now the page's closing element. The
              * shared CTA line is prefixed with "Let's work together."
              * via the `ctaPrefix` prop so the section reads as a proper
              * close on this page without changing the default behaviour
              * for future service pages (other pages with their own
              * final CTAs will simply omit `ctaPrefix`).
              */}
            <AboutElizavetaSection
                middleLine={ABOUT_MIDDLE.clinic}
                inquirySource="clinic-about"
                testIdPrefix="clinic-about"
                ctaPrefix="Let's work together."
            />
        </div>
    );
}
